import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Loader2, MessageCircle, PartyPopper } from "lucide-react";
import { FORM_DESTINATIONS, waLink } from "@/data/site";
import { trackSchedule } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [];
for (let h = 9; h < 18; h++) {
  SLOTS.push(`${String(h).padStart(2, "0")}:00`, `${String(h).padStart(2, "0")}:30`);
}

const inputCls =
  "w-full rounded-lg border border-slate-600/50 bg-navy-950/70 px-4 py-3 text-sm font-light text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-gold-400/70";

const toISO = (d) => d.toISOString().slice(0, 10);
const today = toISO(new Date());
const maxDate = toISO(new Date(Date.now() + 45 * 86400000));

const fmtBR = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const BookingWidget = () => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [taken, setTaken] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", destination: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isWeekend =
    date && [0, 6].includes(new Date(`${date}T12:00:00`).getDay());

  useEffect(() => {
    setTime("");
    if (date && !isWeekend) {
      axios
        .get(`${API}/bookings/availability`, { params: { date } })
        .then((r) => setTaken(r.data.taken || []))
        .catch(() => setTaken([]));
    }
  }, [date, isWeekend]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, date, time });
      trackSchedule();
      setDone({ date, time, name: form.name });
      toast.success("Consultoria pré-agendada!", {
        description: `Confirmaremos seu horário de ${fmtBR(date)} às ${time} pelo WhatsApp.`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Não foi possível agendar agora.", {
        description: "Tente outro horário ou fale conosco pelo WhatsApp.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="py-10 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-gold-400/50 bg-gold-400/10">
          <PartyPopper className="h-7 w-7 text-gold-300" />
        </div>
        <h3 className="font-serif-display gold-text text-3xl">Horário reservado!</h3>
        <p className="mx-auto mt-4 max-w-sm text-sm font-light leading-relaxed text-slate-400">
          {done.name.split(" ")[0]}, sua consultoria está pré-agendada para{" "}
          <span className="text-gold-200">{fmtBR(done.date)} às {done.time}</span>.
          Nossa equipe confirmará pelo WhatsApp.
        </p>
        <a
          data-testid="booking-success-whatsapp"
          href={waLink(`Olá! Acabei de pré-agendar uma consultoria para ${fmtBR(done.date)} às ${done.time} pelo site.`)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-200"
        >
          <MessageCircle className="h-4 w-4" />
          Confirmar no WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={submit}>
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-gold-400/20 bg-gold-400/5 px-4 py-3">
        <CalendarDays className="h-4 w-4 shrink-0 text-gold-400" />
        <p className="text-xs font-light text-slate-400">
          Atendimento de <span className="text-gold-200">segunda a sexta, 9h às 18h</span> —
          consultorias de 30 minutos por videochamada ou WhatsApp.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            ESCOLHA O DIA
          </label>
          <input
            data-testid="booking-date-input"
            type="date"
            required
            min={today}
            max={maxDate}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`${inputCls} [color-scheme:dark]`}
          />
          {isWeekend && (
            <p data-testid="booking-weekend-warning" className="mt-2 text-xs text-amber-400">
              Atendemos de segunda a sexta — escolha um dia útil.
            </p>
          )}
        </div>
        <div>
          <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            NOME COMPLETO
          </label>
          <input
            data-testid="booking-input-name"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Seu nome"
            className={inputCls}
          />
        </div>
      </div>

      {date && !isWeekend && (
        <div className="mt-6">
          <label className="mb-3 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            HORÁRIOS DISPONÍVEIS
          </label>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {SLOTS.map((s) => {
              const disabled = taken.includes(s);
              const active = time === s;
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`booking-slot-${s.replace(":", "")}`}
                  disabled={disabled}
                  onClick={() => setTime(s)}
                  className={`rounded-lg border py-2 font-mono-tech text-xs transition-colors duration-200 ${
                    active
                      ? "border-gold-400 bg-gold-400 text-navy-950"
                      : disabled
                        ? "cursor-not-allowed border-slate-800 text-slate-700 line-through"
                        : "border-slate-600/60 text-slate-300 hover:border-gold-400/60 hover:text-gold-200"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            WHATSAPP COM DDD
          </label>
          <input
            data-testid="booking-input-phone"
            required
            value={form.phone}
            onChange={set("phone")}
            placeholder="(11) 90000-0000"
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            E-MAIL
          </label>
          <input
            data-testid="booking-input-email"
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="voce@email.com"
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            DESTINO DE INTERESSE (OPCIONAL)
          </label>
          <select
            data-testid="booking-select-destination"
            value={form.destination}
            onChange={set("destination")}
            className={`${inputCls} appearance-none ${form.destination ? "" : "text-slate-500"}`}
          >
            <option value="">Selecione (opcional)</option>
            {FORM_DESTINATIONS.map((d) => (
              <option key={d} value={d} className="bg-navy-950 text-slate-100">
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        data-testid="booking-submit-button"
        type="submit"
        disabled={loading || !date || !time || isWeekend}
        className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gold-400 px-6 py-3.5 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
        {loading ? "Reservando..." : "Reservar horário"}
      </button>
    </form>
  );
};
